import type { HostDataStore, OfflineSceneKey, OfflineViewState, View } from "@novorender/api";
import type { Bookmark } from "@novorender/data-js-api";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import type { AuthScope, ModelTreePermission } from "apis/dataV2/authTypes";
import type { DeviationProjectConfig } from "apis/dataV2/deviationTypes";
import type { Permission } from "apis/dataV2/permissions";
import type { ProjectInfo } from "apis/dataV2/projectTypes";
import { telemetry } from "telemetry";
import type { BookmarkCatalogEntry } from "types/bookmark";

import {
    type BookmarkScope,
    type CommittedDeviations,
    type CoordinateSystemSnapshot,
    HOST_DATA_SCHEMA_VERSION,
    type HostDataMeta,
    hostDataNames,
    type ModelTreePermissionsSnapshot,
    type PermissionsSnapshot,
    type ProjectSnapshot,
    type RawSceneConfig,
} from "./schema";

/**
 * The open scene's host data, as the data-access layer reads it offline ({@link networkFirst}).
 * The capture's object ids are valid for the version it is of, so it is read only while the
 * viewer has that version loaded: other ids would select the wrong objects. A read for any
 * project or version but the registered one finds nothing.
 */
type Registration = {
    readonly projectId: string;
    /** The open scene's store, or `undefined` while the scene has no offline directory. */
    readonly hostData: () => HostDataStore | undefined;
    /** The loaded scene's version: its scene index's `offline.manifest` (see {@link manifestIdOfUrl}). */
    readonly loadedManifestId: () => string | undefined;
    /** The signed-in user, who alone reads the personal bookmarks captured for them. */
    readonly user: () => string | undefined;
};

let registered: Registration | undefined;

/** Registers the open scene's host data. Returns the unregister. */
export function registerOfflineHostData(registration: Registration): () => void {
    registered = registration;
    return () => {
        if (registered === registration) {
            registered = undefined;
        }
    };
}

/**
 * Registers the open scene's host data as the viewer finds it: in `offlineState`'s scene for
 * `sceneKey`, looked up per read (a sync completed in this session is read from at once), and
 * read only while the engine has the captured version loaded. Returns the unregister.
 */
export function registerOpenSceneHostData({
    projectId,
    offlineState,
    sceneKey,
    view,
    user,
}: {
    projectId: string;
    offlineState: OfflineViewState;
    sceneKey: OfflineSceneKey;
    view: View | undefined;
    user: Registration["user"];
}): () => void {
    return registerOfflineHostData({
        projectId,
        hostData: () => offlineState.getScene(sceneKey)?.hostData,
        // The version the engine loaded, from the network or, failing that, the offline copy.
        // Unknown until it has loaded one.
        loadedManifestId: () => {
            const manifestUrl = view?.offline?.manifestUrl;
            return manifestUrl && manifestIdOfUrl(manifestUrl);
        },
        user,
    });
}

/**
 * The manifest id a manifest url names: the engine builds it from the scene index's
 * `offline.manifest`, a single file name, as the last path segment (or, on Procore's file
 * service, the `file` parameter).
 */
export function manifestIdOfUrl(url: URL): string | undefined {
    const name = url.searchParams.get("file") ?? url.pathname.split("/").pop();
    if (!name) {
        return undefined;
    }
    try {
        return decodeURIComponent(name);
    } catch {
        return undefined;
    }
}

/** Reports a storage failure, which reads as nothing: the caller's network error then stands. */
function reportReadFailure(error: unknown): undefined {
    // `HostDataStore` rejects with plain `OfflineError` data on an I/O failure.
    telemetry.trackCaughtException({
        source: "offline.offlineHostData.read",
        category: "data",
        error,
    });
    return undefined;
}

/**
 * The open scene's committed capture, if `projectId` is the open scene, the capture is one this
 * schema reads, and it is of the scene version the viewer has loaded. Never rejects.
 * @param anyVersion Whatever version it is of: for a caller that checks `meta.manifestId` against
 *   the version the engine loads before it keeps anything read (see {@link capturedSceneOpen}).
 */
async function openCapture(
    projectId: string,
    { anyVersion = false } = {},
): Promise<{ store: HostDataStore; meta: HostDataMeta; registration: Registration } | undefined> {
    const registration = registered?.projectId === projectId ? registered : undefined;
    const store = registration?.hostData();
    if (!registration || !store) {
        return undefined;
    }
    try {
        const meta = await store.read<HostDataMeta>(hostDataNames.meta);
        const loadedManifestId = registration.loadedManifestId();
        return meta?.schemaVersion === HOST_DATA_SCHEMA_VERSION &&
            meta.projectId === projectId &&
            (anyVersion || (loadedManifestId !== undefined && meta.manifestId === loadedManifestId))
            ? { store, meta, registration }
            : undefined;
    } catch (error) {
        return reportReadFailure(error);
    }
}

/**
 * The scene version a scene's committed capture is of, for the offline widget: `null` if it has
 * none the readers here read -- none committed, one of another schema or project, or one the
 * storage failed to read. The readers then read nothing of it, and the scene doesn't open offline.
 * Never rejects.
 * @param projectId The scene the capture should be of.
 */
export async function readCapturedManifestId(hostData: HostDataStore, projectId: string): Promise<string | null> {
    try {
        const meta = await hostData.read<HostDataMeta>(hostDataNames.meta);
        return meta?.schemaVersion === HOST_DATA_SCHEMA_VERSION && meta.projectId === projectId
            ? meta.manifestId
            : null;
    } catch (error) {
        reportReadFailure(error);
        return null;
    }
}

/** The blob `name` of the open scene's committed capture (see {@link openCapture}). Never rejects. */
async function read<T>(projectId: string, name: string): Promise<T | undefined> {
    const capture = await openCapture(projectId);
    return capture?.store.read<T>(name).catch(reportReadFailure);
}

/**
 * Whether the capture holds `scope`'s bookmarks for whoever reads now: those the user could read
 * when it was taken, and the personal ones only for the user they were captured for.
 */
function holdsBookmarks(
    { meta, registration }: { meta: HostDataMeta; registration: Registration },
    scope: BookmarkScope,
) {
    return (
        meta.bookmarks.captured &&
        (scope === "public" ||
            (meta.bookmarks.personalUser !== null && meta.bookmarks.personalUser === registration.user()))
    );
}

export const offlineHostData = {
    /** A scope the capture doesn't hold for this user reads as empty, not missing. */
    async bookmarkCatalog(projectId: string, scope: BookmarkScope): Promise<BookmarkCatalogEntry[] | undefined> {
        const capture = await openCapture(projectId);
        if (!capture) {
            return undefined;
        }
        if (!holdsBookmarks(capture, scope)) {
            return [];
        }
        return capture.store
            .read<BookmarkCatalogEntry[]>(hostDataNames.bookmarkCatalog(scope))
            .catch(reportReadFailure);
    },
    async bookmarkState(projectId: string, scope: BookmarkScope, bookmarkId: string): Promise<Bookmark | undefined> {
        const capture = await openCapture(projectId);
        if (!capture || !holdsBookmarks(capture, scope)) {
            return undefined;
        }
        return capture.store.read<Bookmark>(hostDataNames.bookmarkState(scope, bookmarkId)).catch(reportReadFailure);
    },
    groupIds(projectId: string, groupId: string): Promise<number[] | undefined> {
        return read(projectId, hostDataNames.groupIds(groupId));
    },
    /** `null` where the server answered with an empty body: none saved. */
    deviationProfiles(projectId: string): Promise<DeviationProjectConfig | null | undefined> {
        return read(projectId, hostDataNames.deviationProfiles);
    },
    committedDeviations(projectId: string): Promise<CommittedDeviations | undefined> {
        return read(projectId, hostDataNames.committedDeviations);
    },
};

/** A blob of the capture, and the scene version the capture is of. */
export type Captured<T> = { readonly data: T; readonly manifestId: string };

/**
 * The scene-open payloads of the open scene's capture, for the standalone scene open. Read before
 * the engine has loaded a version, so each comes with the capture's, and the caller keeps it only
 * if the engine then loads that one (`keepIfVersion` in `sceneOpen.ts`). Each is read only if it
 * was captured with the arguments asked for now. Never rejects.
 */
export const capturedSceneOpen = {
    /** The version of the capture, if the open scene has one this schema reads. */
    async manifestId(projectId: string): Promise<string | undefined> {
        return (await openCapture(projectId, { anyVersion: true }))?.meta.manifestId;
    },
    sceneConfig(projectId: string): Promise<Captured<RawSceneConfig> | undefined> {
        return readCaptured<RawSceneConfig>(projectId, hostDataNames.sceneConfig, () => true);
    },
    async project(projectId: string, args: ProjectSnapshot["args"]): Promise<Captured<ProjectInfo | null> | undefined> {
        return snapshotData(
            await readCaptured<ProjectSnapshot>(
                projectId,
                hostDataNames.project,
                (snapshot) => snapshot.args.projectId === args.projectId,
            ),
        );
    },
    async modelTreePermissions(
        projectId: string,
        args: ModelTreePermissionsSnapshot["args"],
    ): Promise<Captured<ModelTreePermission[] | null> | undefined> {
        return snapshotData(
            await readCaptured<ModelTreePermissionsSnapshot>(
                projectId,
                hostDataNames.modelTreePermissions,
                (snapshot) => snapshot.args.projectId === args.projectId,
            ),
        );
    },
    /** Those granted of every permission, for the permission check of `scope`. */
    async permissions(projectId: string, scope: AuthScope): Promise<Captured<Permission[]> | undefined> {
        return snapshotData(
            await readCaptured<PermissionsSnapshot>(projectId, hostDataNames.permissions, (snapshot) =>
                sameScope(snapshot.args.scope, scope),
            ),
        );
    },
    /** The definitions of the coordinate system `epsg`. */
    async coordinateSystem(
        projectId: string,
        epsg: string,
    ): Promise<Captured<CoordinateSystemSnapshot["data"]> | undefined> {
        return snapshotData(
            await readCaptured<CoordinateSystemSnapshot>(
                projectId,
                hostDataNames.coordinateSystem,
                (snapshot) => snapshot.args.epsg === epsg,
            ),
        );
    },
};

async function readCaptured<T>(
    projectId: string,
    name: string,
    answers: (value: T) => boolean,
): Promise<Captured<T> | undefined> {
    const capture = await openCapture(projectId, { anyVersion: true });
    const value = await capture?.store.read<T>(name).catch(reportReadFailure);
    if (!capture || value === undefined || !answers(value)) {
        return undefined;
    }
    // Another tab's sync may have committed a capture between the two reads: the blob is then of
    // the version the meta now names, which must be the one it was read with.
    const meta = await capture.store.read<HostDataMeta>(hostDataNames.meta).catch(reportReadFailure);
    return meta?.manifestId === capture.meta.manifestId ? { data: value, manifestId: meta.manifestId } : undefined;
}

function snapshotData<T>(captured: Captured<{ data: T }> | undefined): Captured<T> | undefined {
    return captured && { data: captured.data.data, manifestId: captured.manifestId };
}

/** The same scope, as JSON has it: a key whose value is `undefined` is no key. */
function sameScope(a: AuthScope, b: AuthScope): boolean {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)] as (keyof AuthScope)[]);
    return [...keys].every((key) => a[key] === b[key]);
}

type QueryResult<T> = { data: T; error?: undefined } | { error: FetchBaseQueryError; data?: undefined };

/**
 * A data-v2 query's result. Online it is the server's, errors included. Offline it is the open
 * scene's host data, or the network's when the host data has nothing.
 */
export async function networkFirst<T>({
    isOnline,
    network,
    offline,
}: {
    isOnline: boolean;
    network: () => Promise<QueryResult<T>>;
    offline: () => Promise<T | undefined>;
}): Promise<QueryResult<T>> {
    if (isOnline) {
        return network();
    }
    const data = await offline();
    return data !== undefined ? { data } : network();
}

/**
 * The scene's committed deviation config (its `deviations.json` asset). Online it comes from the
 * network. Offline it comes from the host data, or the network when the host data has none.
 * `undefined` when the scene has none, as a response that isn't OK says online.
 */
export async function loadCommittedDeviations({
    url,
    projectId,
    isOnline,
}: {
    url: string;
    projectId: string;
    isOnline: boolean;
}): Promise<DeviationProjectConfig | undefined> {
    const fromCapture = async () => {
        const captured = await offlineHostData.committedDeviations(projectId);
        return captured && { config: captured.config ?? undefined };
    };

    if (!isOnline) {
        const captured = await fromCapture();
        if (captured) {
            return captured.config;
        }
    }
    const res = await fetch(url);
    return res.ok ? ((await res.json()) as DeviationProjectConfig) : undefined;
}
